import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  InputAdornment,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { useNavigate } from "react-router-dom";
import ChatAvatar from "./ChatAvatar";
import { formatListTime } from "./chatFormat";
import { useAuthStore } from "./store/useAuthStore";
import { useChatStore } from "./store/useChatStore";

const ListSkeleton = () => (
  <Box>
    {[0, 1, 2, 3].map((row) => (
      <Box key={row} display={"flex"} gap={1.5} alignItems={"center"} px={2} py={1.5}>
        <Skeleton variant="circular" width={44} height={44} />
        <Box width={"100%"}>
          <Skeleton variant="text" width={"45%"} />
          <Skeleton variant="text" width={"75%"} />
        </Box>
      </Box>
    ))}
  </Box>
);

const SideBar = ({ onSelectConversation }) => {
  const { selectedUser, setSelectedUser, users, isUsersLoading } =
    useChatStore();
  const { onlineUsers } = useAuthStore();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const conversations = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users || [];
    return (users || []).filter((conversation) =>
      (conversation?.recipient?.name || "").toLowerCase().includes(term),
    );
  }, [users, search]);

  const hasChats = users && users.length > 0;

  const selectConversation = (conversation) => {
    setSelectedUser(conversation);
    if (onSelectConversation) onSelectConversation(conversation);
  };

  if (isUsersLoading) {
    return (
      <Box height={"100%"} bgcolor={"#fff"}>
        <ListSkeleton />
      </Box>
    );
  }

  if (!hasChats) {
    return (
      <Box
        height={"100%"}
        bgcolor={"#fff"}
        display={"flex"}
        flexDirection={"column"}
        justifyContent={"center"}
        alignItems={"center"}
        textAlign={"center"}
        gap={1}
        px={3}
      >
        <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 52, color: "#ffb877" }} />
        <Typography variant="h6" fontWeight={600}>
          No chats yet
        </Typography>
        <Typography fontSize={14} color="text.secondary">
          You have no chat history. Start a conversation from a profile you like
          and it will show up here.
        </Typography>
        <Button
          variant="outlined"
          sx={{ mt: 1 }}
          onClick={() => navigate("/auth/home")}
        >
          Browse matches
        </Button>
      </Box>
    );
  }

  return (
    <Box
      height={"100%"}
      bgcolor={"#fff"}
      display={"flex"}
      flexDirection={"column"}
      minHeight={0}
    >
      <Box px={2} pt={2} pb={1.5}>
        <Typography variant="h6" fontWeight={700} mb={1.5}>
          Messages
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search conversations"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" color="disabled" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
        />
      </Box>

      <Box flex={1} overflow={"auto"} minHeight={0}>
        {conversations.length === 0 ? (
          <Typography
            fontSize={14}
            color="text.secondary"
            textAlign={"center"}
            px={3}
            py={4}
          >
            No conversations match "{search.trim()}".
          </Typography>
        ) : (
          conversations.map((conversation) => {
            const recipient = conversation?.recipient;
            const isSelected =
              selectedUser?.conversation_id === conversation?.conversation_id;

            return (
              <ButtonBase
                key={conversation.conversation_id}
                onClick={() => selectConversation(conversation)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  width: "100%",
                  px: 2,
                  py: 1.25,
                  textAlign: "left",
                  borderLeft: "3px solid",
                  borderLeftColor: isSelected ? "primary.main" : "transparent",
                  bgcolor: isSelected ? "#fff5eb" : "transparent",
                  "&:hover": { bgcolor: isSelected ? "#fff5eb" : "#f7f7f8" },
                }}
              >
                <ChatAvatar
                  participant={recipient}
                  showPresence
                  isOnline={onlineUsers.includes(recipient?.id)}
                />
                <Box flex={1} minWidth={0}>
                  <Box display={"flex"} alignItems={"center"} gap={1}>
                    <Typography
                      flex={1}
                      noWrap
                      fontSize={15}
                      fontWeight={isSelected ? 700 : 600}
                    >
                      {recipient?.name || "Pellifix user"}
                    </Typography>
                    <Typography
                      fontSize={12}
                      color="text.secondary"
                      flexShrink={0}
                    >
                      {formatListTime(conversation?.latest_time)}
                    </Typography>
                  </Box>
                  <Typography
                    noWrap
                    fontSize={13}
                    color="text.secondary"
                    fontStyle={conversation?.message ? "normal" : "italic"}
                  >
                    {conversation?.message || "No messages yet"}
                  </Typography>
                </Box>
              </ButtonBase>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default SideBar;
